import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../client/apiClient';
import { GetUiElementPermissionsResponseSchemaType } from '../types/dto/uiElementPermissions';

export const uiElementPermissions = async () => {
  const { data } = await apiClient.GET('/authorization/permissions');
  return data as GetUiElementPermissionsResponseSchemaType;
};

/**
 * Capabilities are keyed on the user, but they are no longer a property OF the user — they come from
 * grants, which are edited out-of-band with no event reaching the browser. So a grant, a revocation or an
 * organization change leaves this cache describing a world that no longer exists.
 *
 * Refetching on focus and reconnect is the whole fix, deliberately, rather than an invalidation protocol:
 * this response drives **affordances**, never access. The server re-resolves the principal on every
 * request with no cross-request cache, so a stale entry can only ever show a button that then refuses —
 * it cannot grant anything. Trading a socket or a poll for that would buy correctness we already have.
 */
export const useUiElementPermissionsQuery = (userId: string | null | undefined) => {
  return useQuery<GetUiElementPermissionsResponseSchemaType>({
    queryKey: ['uiElementPermissions', userId],
    queryFn: () => uiElementPermissions(),
    enabled: !!userId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Holds nothing and constrains nothing until the real answer arrives: a placeholder that named an
    // empty permitted set would blank the workspace for a moment on every load.
    placeholderData: {
      authorizedUiElements: [],
      canCrossOrganizations: false,
      canManageOrganizations: false,
      capabilities: [],
      organization: null
    }
  });
};
